/** Areas an administrator can switch on or off per person (server: auth/access.ts). */
export const ACCESS_MODULES = ['billing', 'reports', 'stock.view', 'stock.manage', 'stock.ledger', 'lab.recipes', 'lab.complete'] as const
export type AccessModule = typeof ACCESS_MODULES[number]

export interface ApiError {
  status: number
  message: string
}

export class ApiFailure extends Error {
  status: number
  /** The server's machine-readable reason, e.g. SLOT_TAKEN, when it sent one. */
  code?: string
  /** The rest of the error body: `issues`, `overridable`, `field`… */
  details: Record<string, unknown>
  constructor(status: number, message: string, code?: string, details: Record<string, unknown> = {}) {
    super(message)
    this.name = 'ApiFailure'
    this.status = status
    this.code = code
    this.details = details
  }
}

/**
 * Called when the server says the session is gone (401) on an ordinary
 * request - it expired, was revoked, or the account was switched off. The
 * auth context registers this so the app signs out on the spot instead of
 * leaving a screen full of failures. Sign-in routes are excluded: a 401 there
 * is a wrong password, not an ended session.
 */
let onSessionEnded: ((path: string) => void) | null = null
export function setSessionEndedHandler(fn: ((path: string) => void) | null): void {
  onSessionEnded = fn
}
const SIGN_IN_PATHS = ['/api/auth/login', '/api/auth/otp/', '/api/auth/me', '/api/auth/change-password']

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response | undefined
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      res = await fetch(path, {
        ...init,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(init?.headers ?? {}),
        },
      })
      break
    } catch {
      if (attempt === 0) {
        // Vite and the API can come up a moment apart during local/mobile startup.
        // One short retry avoids presenting a transient boot race as an outage.
        await new Promise((resolve) => setTimeout(resolve, 250))
        continue
      }
      throw new ApiFailure(0, 'Cannot reach the server. Check that it is running.')
    }
  }
  if (!res) throw new ApiFailure(0, 'Cannot reach the server. Check that it is running.')

  if (res.status === 204) return undefined as T

  const text = await res.text()

  // Never let a non-JSON body escape as a raw SyntaxError: every caller
  // checks `instanceof ApiFailure`, and a proxy error page is not JSON.
  let body: unknown = {}
  if (text) {
    try {
      body = JSON.parse(text) as unknown
    } catch {
      body = null
    }
  }

  if (!res.ok) {
    if (res.status === 401 && onSessionEnded && !SIGN_IN_PATHS.some((p) => path.startsWith(p))) {
      onSessionEnded(path)
    }
    const fromServer =
      typeof body === 'object' && body && 'error' in body
        ? String((body as { error: unknown }).error)
        : null

    // A dev proxy turns an unreachable API into an empty 500, and a gateway
    // turns it into a 502/503/504. Saying "something went wrong" there sends
    // people hunting for a bug in the app when the server is simply not up.
    const unreachable =
      !fromServer && (res.status === 502 || res.status === 503 || res.status === 504
        || (res.status === 500 && text.trim() === ''))

    const message = fromServer
      ?? (unreachable
        ? 'Cannot reach the server. Check that it is running.'
        : `Something went wrong (${res.status}).`)

    const details = typeof body === 'object' && body ? body as Record<string, unknown> : {}
    throw new ApiFailure(
      res.status, message, typeof details.code === 'string' ? details.code : undefined, details,
    )
  }

  return body as T
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

export type Role = 'admin' | 'doctor' | 'receptionist' | 'patient' | 'platform_admin' | 'lab_manager'

export interface User {
  id: string
  username: string
  fullName: string
  profileImageUrl?: string | null
  role: Role
  /** The clinic this account belongs to; null for platform admins. */
  clinicId?: string | null
  /** What the clinic calls itself, for the header. Null for platform admins. */
  clinicName?: string | null
  /** Sign-in identifiers besides the username. */
  mobile?: string | null
  email?: string | null
  doctorId: string | null
  patientId: string | null
  isActive?: boolean
  mustChangePassword: boolean
  /** Allowed to update stock: the stock.manage module. */
  canManageStock?: boolean
  /** Module access changed from the role's defaults (stored), in the staff list. */
  access?: Partial<Record<AccessModule, boolean>>
  lastLoginAt?: string | null
  createdAt?: string
  /** What this account may do, from the server's permission table (signed-in user only). */
  permissions?: Permission[]
}

/** Mirrors server/src/auth/permissions.ts. The server enforces; the app only shows and hides. */
export type Permission =
  | 'users.manage' | 'audit.view' | 'clinic.configure' | 'reports.view'
  | 'patients.read' | 'patients.write' | 'appointments.manage' | 'appointments.own'
  | 'schedule.manage' | 'schedule.own' | 'consult.write' | 'billing.manage'
  | 'records.own' | 'profile.own' | 'lab.work'

/** Roles an administrator can hand out. Platform admins are made from the command line. */
export const ASSIGNABLE_ROLES: Role[] = ['admin', 'doctor', 'receptionist', 'patient', 'lab_manager']

/** One account in user management, with what it links to and how it is used. */
export interface UserDetail extends User {
  doctorName: string | null
  patientName: string | null
  patientCode: string | null
  clinicName: string | null
  activeSessions: number
  updatedAt: string
}

/** One line of an account's audit trail. */
export interface UserActivity {
  id: string
  entity: string
  entityId: string | null
  action: string
  after: Record<string, unknown> | null
  ip: string | null
  createdAt: string
  byUserId: string | null
  byName: string | null
}

/** The reply to asking for a one-time code. `devCode` exists only on a development server. */
export interface OtpRequestResult {
  ok: true
  message: string
  expiresInSeconds: number
  resendInSeconds: number
  devCode?: string
}

export interface Doctor {
  id: string
  fullName: string
  qualifications: string | null
  registrationNo: string | null
  specialization: string | null
  /** Optional; only picks the drawn picture when there is no photo. */
  gender?: Gender | null
  phone: string | null
  email: string | null
  /**
   * The portrait an administrator set on the roster. Edit this one; it is
   * the only value the server will accept back.
   */
  photoUrl: string | null
  /**
   * The portrait to *show*: the roster photo, or the doctor's own profile
   * picture when the roster has none. Read-only - writing it back would fail
   * validation, since a profile picture is a data URL. Null is still normal,
   * and initials stand in.
   */
  portraitUrl: string | null
  /** A few plain-text sentences a patient reads before choosing a doctor. */
  bio: string | null
  consultationFee: string
  followUpFee: string
  followUpWindowDays: number
  displayOrder: number
  isActive: boolean
}

export interface Letterhead {
  id: string
  doctorId: string
  mode: 'preprinted' | 'full'
  marginTopMm: string
  marginBottomMm: string
  marginLeftMm: string
  marginRightMm: string
  showVitalsStrip: boolean
  showAllergyBanner: boolean
  headerNote: string | null
  footerNote: string | null
}

export interface ClinicSettings {
  id: number
  clinicName: string
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  state: string | null
  pincode: string | null
  phone: string | null
  altPhone: string | null
  email: string | null
  opdTimings: string | null
  currency: string
  financialYearStartMonth: number
  receiptPrefix: string
  patientCodePrefix: string
  defaultSlotMinutes: number
  /** Off: doctor bookings go by serial, the exact time is optional. */
  appointmentTimeRequired?: boolean
  /** Off: a lab test may be booked for any time on a day. */
  labTimeRequired?: boolean
  /** Off: the clinic runs on billing alone; booking screens are hidden. */
  appointmentsEnabled?: boolean
}

export type Gender = 'male' | 'female' | 'other'
export type VisitType = 'new' | 'follow_up' | 'review' | 'emergency'
export type VisitStatus = 'waiting' | 'in_consult' | 'completed' | 'left_without_seeing'
export type PaymentMode =
  | 'cash' | 'upi' | 'card' | 'net_banking' | 'insurance' | 'free'
  /** Money taken outside the system - bank transfer, cheque, direct UPI. */
  | 'offline'

/**
 * Where a booking stands on money. The clinic takes payment at the desk, so a
 * booking carries an intent - the mode chosen and the fee quoted - and turns
 * `settled` once the receipt exists. Only the receipt is revenue.
 */
export type BookingPaymentStatus = 'pending' | 'settled'

/**
 * What a patient may choose when booking for themselves; the server enforces
 * the same list. Insurance needs a claim verified at the clinic and a waiver
 * is the clinic's call, so neither is offered here.
 */
export const PATIENT_PAYMENT_MODES: readonly PaymentMode[] = ['cash', 'upi', 'card', 'net_banking']

/**
 * The two lines a patient is registered under. They are separate values, never
 * one combined field. `null` is only a patient registered before categories
 * existed.
 */
export type PatientCategory = 'diabetes' | 'non_diabetic'

export const PATIENT_CATEGORIES: readonly PatientCategory[] = ['diabetes', 'non_diabetic']

export const PATIENT_CATEGORY_LABEL: Record<PatientCategory, string> = {
  diabetes: 'Diabetic',
  non_diabetic: 'Non-Diabetic',
}

/** What to print for a stored category; a patient with none recorded reads "Not set". */
export function categoryText(c: PatientCategory | null | undefined): string {
  return c ? PATIENT_CATEGORY_LABEL[c] : 'Not set'
}

export interface Patient {
  id: string
  patientCode: string
  fullName: string
  /** Null when the patient has no phone: elderly, children and walk-ins often do not. */
  mobile: string | null
  altMobile: string | null
  gender: Gender
  category: PatientCategory | null
  ageYears: number | null
  ageMonths: number | null
  dateOfBirth: string | null
  bloodGroup: string | null
  addressLine: string | null
  city: string | null
  pincode: string | null
  guardianName: string | null
  occupation?: string | null
  referredBy: string | null
  allergies: string | null
  chronicConditions: string | null
  currentMedication: string | null
  notes: string | null
}

export interface PatientMatch extends Patient {
  visitCount: number
  lastVisitDate: string | null
}

export interface VisitPreview {
  visitNo: number
  visitNoWithDoctor: number
  visitType: VisitType
  daysSinceLastVisit: number | null
  lastVisitDate: string | null
  fee: string
  followUpWindowDays: number
  summary: string
}

export interface Visit {
  id: string
  visitNo: number
  visitType: VisitType
  tokenNo: number
  visitDate: string
  status: VisitStatus
  chiefComplaint: string | null
  arrivedAt: string
  patientId: string
  doctorId: string
}

export interface QueueRow {
  id: string
  visitNo: number
  visitType: VisitType
  tokenNo: number
  status: VisitStatus
  chiefComplaint: string | null
  arrivedAt: string
  patientId: string
  patientName: string
  patientCode: string
  mobile: string | null
  gender: Gender
  category: PatientCategory | null
  ageYears: number | null
  ageMonths: number | null
  allergies: string | null
  doctorId: string
  doctorName: string
  paymentStatus: string | null
  amountPaid: string | null
  netPayable: string | null
}

export interface Vitals {
  id: string
  visitId: string
  recordedAt: string
  weightKg: string | null
  heightCm: string | null
  bmi: string | null
  bpSystolic: number | null
  bpDiastolic: number | null
  pulseBpm: number | null
  temperatureF: string | null
  spo2Pct: number | null
  respiratoryRate: number | null
  bloodSugar: string | null
  bloodSugarType: string | null
  notes: string | null
}

export interface Payment {
  id: string
  receiptNo: string
  fee: string
  discount: string
  discountReason: string | null
  netPayable: string
  amountPaid: string
  mode: PaymentMode
  referenceNo: string | null
  status: string
  collectedAt: string
}

/**
 * What is still owed on a visit, rounded to paise before comparing so
 * floating point never invents a one-paisa balance on a settled receipt.
 * Returns 0 when nothing is due, which is also the "hide the line" signal.
 */
export function balanceDueOf(payment: Payment | null | undefined): number {
  if (!payment) return 0
  const due = Number(payment.netPayable) - Number(payment.amountPaid)
  return Math.max(0, Math.round(due * 100) / 100)
}

/**
 * A record the newly registered patient resembles: same mobile, or the same
 * name. A warning for the desk, never a refusal (PAT-05).
 */
/**
 * The day-end drawer count. `counted` and `discrepancy` are null until a
 * count is supplied; discrepancy is positive for a surplus, negative when
 * the drawer is short.
 */
export interface CashReconciliation {
  date: string
  cashCollected: string
  cashRefunded: string
  expected: string
  counted: string | null
  discrepancy: string | null
}

export interface PossibleDuplicate {
  id: string
  patientCode: string
  fullName: string
  mobile: string | null
}

export type InvoiceStatus = 'draft' | 'finalised' | 'void'
/** Whether the money owed has arrived: none of it, some, or all. */
export type Settlement = 'unpaid' | 'partial' | 'paid'

export interface InvoiceItem {
  id: string
  description: string
  quantity: number
  unitPrice: string
  /** An absolute amount off this line, not a percentage. */
  discount: string
  taxRate: string
  taxAmount: string
  lineTotal: string
}

/** A line being edited, before the server has priced it. */
export interface DraftItem {
  description: string
  quantity: string
  unitPrice: string
  discount: string
  taxRate: string
}

export interface Invoice {
  id: string
  invoiceNo: string
  visitId: string | null
  appointmentId: string | null
  patientId: string
  status: InvoiceStatus
  subtotal: string
  discountTotal: string
  taxTotal: string
  total: string
  discountReason: string | null
  notes: string | null
  finalisedAt: string | null
  voidedAt: string | null
  voidReason: string | null
  createdAt: string
}

export interface InvoiceRefund {
  id: string
  paymentId: string
  type: string
  /** Signed: a refund is negative. */
  amount: string
  reason: string | null
  createdAt: string
}

/** One bill with everything a billing screen and a print need. */
export interface InvoiceDetail {
  invoice: Invoice
  patient: { id: string; fullName: string; patientCode: string; mobile: string | null }
  items: InvoiceItem[]
  payments: Payment[]
  refunds: InvoiceRefund[]
  totals: {
    paid: string
    refunded: string
    netPaid: string
    outstanding: string
    settlement: Settlement
  }
}

export interface InvoiceListRow {
  id: string
  invoiceNo: string
  status: InvoiceStatus
  total: string
  createdAt: string
  patientName: string
  patientCode: string
  paid: string
  refunded: string
  outstanding: string
  settlement: Settlement
}

export const SETTLEMENT_LABEL: Record<Settlement, string> = {
  unpaid: 'Unpaid',
  partial: 'Part paid',
  paid: 'Paid',
}

/**
 * What a line comes to, mirroring the server's arithmetic so the desk sees
 * the figure move as it types. The server recomputes it and its answer wins;
 * this is only for the running total on screen.
 */
export function lineTotalOf(item: DraftItem): number {
  const gross = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0)
  const net = Math.max(0, gross - (Number(item.discount) || 0))
  const tax = net * ((Number(item.taxRate) || 0) / 100)
  return Math.round((net + tax) * 100) / 100
}

/**
 * The whole bill as it is being typed, in the same four figures a finalised
 * bill shows: gross of the lines, every discount, the tax, the total. Worth
 * mirroring the server's breakdown rather than just summing line totals -
 * otherwise "Items" means the gross while the bill is being raised and the
 * net once it is saved, and the same word shows two numbers for one bill.
 *
 * The server recomputes all of it and its answer wins.
 */
export function quoteInvoice(lines: DraftItem[], billDiscount: string): {
  subtotal: number
  discountTotal: number
  taxTotal: number
  total: number
} {
  const round = (n: number) => Math.round(n * 100) / 100
  let subtotal = 0
  let discounts = Number(billDiscount) || 0
  let taxTotal = 0

  for (const l of lines) {
    const gross = (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0)
    const lineDiscount = Math.min(Number(l.discount) || 0, gross)
    subtotal += gross
    discounts += lineDiscount
    taxTotal += (gross - lineDiscount) * ((Number(l.taxRate) || 0) / 100)
  }

  return {
    subtotal: round(subtotal),
    discountTotal: round(discounts),
    taxTotal: round(taxTotal),
    total: Math.max(0, round(subtotal - discounts + taxTotal)),
  }
}

export interface VisitHistoryRow {
  id: string
  visitNo: number
  visitType: VisitType
  visitDate: string
  tokenNo: number
  status: VisitStatus
  chiefComplaint: string | null
  doctorName: string
  diagnosis: string | null
}

export const VISIT_TYPE_LABEL: Record<VisitType, string> = {
  new: 'New',
  follow_up: 'Follow-up',
  review: 'Review',
  emergency: 'Emergency',
}

export const VISIT_STATUS_LABEL: Record<VisitStatus, string> = {
  waiting: 'Waiting',
  in_consult: 'In consult',
  completed: 'Completed',
  left_without_seeing: 'Left',
}

export const PAYMENT_MODE_LABEL: Record<PaymentMode, string> = {
  cash: 'Cash',
  upi: 'UPI',
  card: 'Card',
  net_banking: 'Net banking',
  insurance: 'Insurance',
  free: 'Free',
  offline: 'Offline',
}

/**
 * The same modes worded for a booking rather than a receipt.
 *
 * Only cash says "at clinic", because cash is the only one that is. The rest
 * are taken in the app the moment the booking is made, so labelling them
 * "UPI at clinic" would promise the opposite of what happens next.
 */
export const BOOKING_PAYMENT_MODE_LABEL: Record<PaymentMode, string> = {
  cash: 'Cash at clinic',
  upi: 'UPI',
  card: 'Card',
  net_banking: 'Net banking',
  insurance: 'Insurance',
  free: 'No charge',
  offline: 'Offline / transfer',
}

/**
 * Money already received outside the system, which staff record with proof.
 *
 * Not offered to patients: it is an assertion that a payment happened, and
 * the clinic is the only party that can make it.
 */
export const OFFLINE_PAYMENT_MODE: PaymentMode = 'offline'

/**
 * Modes that are charged through the gateway at booking time.
 *
 * Cash, insurance and a waived fee are all settled at the desk, so picking
 * one of those books and stops. Anything here opens checkout instead.
 */
export const ONLINE_PAYMENT_MODES = ['upi', 'card', 'net_banking'] as const
export type OnlinePaymentMode = (typeof ONLINE_PAYMENT_MODES)[number]

export function isOnlineMode(mode: PaymentMode | null | undefined): mode is OnlinePaymentMode {
  return mode === 'upi' || mode === 'card' || mode === 'net_banking'
}

export const BOOKING_PAYMENT_STATUS_LABEL: Record<BookingPaymentStatus, string> = {
  pending: 'Pay at clinic',
  settled: 'Paid',
}

/**
 * What to call a booking's payment state.
 *
 * Just the state, because the mode beside it already says how and where:
 * "Cash at clinic · Pay at clinic" said the same thing twice and was long
 * enough to wrap the row on a phone. One word each, and the pair reads
 * "Cash at clinic · Due" or "UPI · Paid".
 */
export function bookingPaymentLabel(status: BookingPaymentStatus): string {
  return status === 'settled' ? 'Paid' : 'Due'
}

/** Age as clinics write it: "43 y", "8 m" for infants. */
export function ageLabel(p: { ageYears: number | null; ageMonths: number | null }): string {
  if (p.ageYears == null && p.ageMonths == null) return '—'
  if ((p.ageYears ?? 0) === 0 && p.ageMonths != null) return `${p.ageMonths} m`
  return `${p.ageYears} y`
}

export function genderShort(g: Gender): string {
  return g === 'male' ? 'M' : g === 'female' ? 'F' : 'O'
}

/* ---------- doctor's console (phase 2) ---------- */

export type MedicineTiming =
  | 'before_food' | 'after_food' | 'with_food' | 'empty_stomach' | 'bedtime'
export type TestUrgency = 'routine' | 'urgent' | 'stat'
export type TestStatus = 'advised' | 'sample_taken' | 'result_awaited' | 'result_received'

export interface Drug {
  id: string
  brandName: string
  genericName: string | null
  strength: string | null
  form: string | null
  defaultDoseMorning: string | null
  defaultDoseAfternoon: string | null
  defaultDoseNight: string | null
  defaultTiming: MedicineTiming | null
  defaultDurationDays: string | null
  notes: string | null
}

export interface TestMasterRow {
  id: string
  name: string
  category: string | null
  sampleType: string | null
  preparation: string | null
  typicalCost: string | null
  labName: string | null
}

export interface PrescriptionMedicine {
  id?: string
  srNo?: number
  drugId: string | null
  drugName: string
  strength: string | null
  form: string | null
  doseMorning: string | null
  doseAfternoon: string | null
  doseNight: string | null
  timing: MedicineTiming | null
  durationDays: number | null
  quantity: string | null
  instructions: string | null
}

export interface PrescriptionTest {
  id?: string
  srNo?: number
  testId: string | null
  testName: string
  urgency: TestUrgency
  instructions: string | null
  status?: TestStatus
  reviewOn: string | null
}

export interface PrescriptionAddendum {
  id: string
  note: string
  createdAt: string
  /** Null only for rows written before the author was carried through. */
  createdByName: string | null
}

export interface Prescription {
  id: string
  visitId: string
  chiefComplaints: string | null
  historyPresentIllness: string | null
  pastHistory: string | null
  familyHistory: string | null
  examinationFindings: string | null
  provisionalDiagnosis: string | null
  icd10Code: string | null
  advice: string | null
  followUpDays: number | null
  followUpDate: string | null
  signedAt: string | null
  isLocked: boolean
  printedAt: string | null
  printCount: number
  medicines: PrescriptionMedicine[]
  tests: PrescriptionTest[]
  addenda: PrescriptionAddendum[]
}

export interface TemplatePayload {
  medicines: PrescriptionMedicine[]
  tests: PrescriptionTest[]
  advice: string | null
  followUpDays: number | null
}

export interface PrescriptionTemplate {
  id: string
  doctorId: string
  name: string
  payload: TemplatePayload
  useCount: number
}

export interface HistoryEntry {
  visit: Visit
  doctorName: string
  vitals: Vitals[]
  prescription: Prescription | null
}

export const MEDICINE_TIMING_LABEL: Record<MedicineTiming, string> = {
  before_food: 'Before food',
  after_food: 'After food',
  with_food: 'With food',
  empty_stomach: 'Empty stomach',
  bedtime: 'Bedtime',
}

export const TEST_URGENCY_LABEL: Record<TestUrgency, string> = {
  routine: 'Routine',
  urgent: 'Urgent',
  stat: 'STAT',
}

/** The morning-afternoon-night pattern every Indian prescription is read in. */
export function doseNotation(
  m: string | null, a: string | null, n: string | null,
): string {
  const box = (v: string | null) => (v ?? '').trim() || '0'
  return `${box(m)}-${box(a)}-${box(n)}`
}

/**
 * Quantity to dispense, mirroring the server's rule so the box fills in as the
 * doctor types rather than only after saving. Returns null for volume doses -
 * a syrup is dispensed in bottles, and a guess would print a wrong number.
 */
export function computeQuantity(
  m: string | null, a: string | null, n: string | null, days: number | null,
): string | null {
  if (!days || days <= 0) return null
  const parse = (v: string | null): number | null => {
    const raw = (v ?? '').trim()
    if (raw === '') return 0
    const frac = raw.match(/^(\d+)\s*\/\s*(\d+)$/)
    if (frac) return Number(frac[2]) === 0 ? null : Number(frac[1]) / Number(frac[2])
    return /^\d+(\.\d+)?$/.test(raw) ? Number(raw) : null
  }
  const doses = [m, a, n].map(parse)
  if (doses.some((d) => d === null)) return null
  const perDay = doses.reduce<number>((s, d) => s + (d ?? 0), 0)
  return perDay > 0 ? String(Math.ceil(perDay * days)) : null
}

/* ---------- scheduling and records (phase 3) ---------- */

export type AppointmentStatus =
  | 'booked' | 'confirmed' | 'arrived' | 'in_consult' | 'completed'
  | 'cancelled' | 'no_show' | 'rescheduled'
export type BookingChannel = 'walk_in' | 'phone' | 'online'

export interface Slot {
  time: string
  iso: string
  booked: boolean
  appointmentId?: string
  patientName?: string
  patientId?: string
  overflow?: boolean
  /** Already started on the clinic's clock. Patients cannot book it. */
  past?: boolean
  /** Reserved while someone pays for it online. Frees itself after a few minutes. */
  held?: boolean
}

export interface ScheduleSession {
  label: string
  startTime: string
  endTime: string
  slotMinutes: number
  maxPatients: number | null
  slots: Slot[]
  bookedCount: number
  overCap: boolean
}

export interface DaySchedule {
  date: string
  dayOfWeek: number
  closed: boolean
  reason: string | null
  sessions: ScheduleSession[]
  bookedTotal: number
}

export interface Appointment {
  id: string
  patientId: string
  doctorId: string
  scheduledAt: string
  durationMin: number
  status: AppointmentStatus
  channel: BookingChannel
  rescheduledFromId: string | null
  rescheduleReason: string | null
  cancelReason: string | null
  notes: string | null
  /** How the patient means to pay. Null when the booking was taken without asking. */
  paymentMode: PaymentMode | null
  /** The fee quoted at booking, set by the server from the doctor's rate card. */
  paymentAmount: string | null
  paymentReference: string | null
  paymentStatus: BookingPaymentStatus
  patientName: string
  patientCode: string
  category?: PatientCategory | null
  mobile: string | null
  ageYears: number | null
  ageMonths: number | null
  gender: Gender
  allergies: string | null
  doctorName: string
  /** Why staff booked outside the schedule rules. Null on an ordinary booking. */
  overrideReason?: string | null
  /** Who made the booking - the patient themselves, or someone at the desk. */
  createdByName?: string | null
  createdByRole?: 'admin' | 'doctor' | 'receptionist' | 'patient' | null
  createdAt?: string
  /** Place in the doctor's day, in slot order. Null once cancelled or moved. */
  serialNo?: number | null
  /** Where that doctor's day has got to. Serials and counts only, no names. */
  queue?: DayQueue | null
}

export interface DayQueue {
  /** The serial in the consulting room now, or null when nobody is. */
  nowServing: number | null
  /** The first patient who has arrived, else the first still expected. */
  next: number | null
  completed: number
  total: number
}

export interface AvailabilitySession {
  id: string
  doctorId: string
  dayOfWeek: number
  sessionLabel: string
  startTime: string
  endTime: string
  slotMinutes: number
  maxPatients: number | null
  isActive: boolean
}

export interface ScheduleOverride {
  id: string
  doctorId: string
  onDate: string
  isClosed: boolean
  startTime: string | null
  endTime: string | null
  slotMinutes: number | null
  maxPatients: number | null
  reason: string | null
}

export interface Holiday {
  id: string
  onDate: string
  name: string
}

export interface RecordRow {
  id: string
  visitDate: string
  tokenNo: number
  visitNo: number
  visitType: VisitType
  status: VisitStatus
  chiefComplaint: string | null
  patientId: string
  patientName: string
  patientCode: string
  mobile: string | null
  gender: Gender
  category: PatientCategory | null
  ageYears: number | null
  ageMonths: number | null
  doctorId: string
  doctorName: string
  diagnosis: string | null
  fee: string | null
  paid: string | null
  paymentStatus: string | null
}

export const APPOINTMENT_STATUS_LABEL: Record<AppointmentStatus, string> = {
  booked: 'Booked',
  confirmed: 'Confirmed',
  arrived: 'Arrived',
  in_consult: 'In consult',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No-show',
  rescheduled: 'Rescheduled',
}

export const DOW_LABEL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** Fetches a CSV (or any file) with the session cookie and saves it locally. */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const res = await fetch(path, { credentials: 'include' })
  if (!res.ok) throw new ApiFailure(res.status, 'Export failed.')
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/* ---------- reports and backups (phase 4) ---------- */

export interface DashboardTotals {
  visits: number
  completed: number
  collected: string
  outstanding: string
}

export interface DashboardReport {
  range: { from: string; to: string }
  totals: DashboardTotals
  byDoctor: { doctorId: string; doctorName: string; count: number; revenue: string }[]
  byType: { visitType: VisitType; count: number }[]
  byMode: { mode: PaymentMode; count: number; total: string }[]
  /** The filter the report was run with; null means both lines. */
  category: PatientCategory | null
  /** Both lines side by side. A null category is a patient registered before categories existed. */
  byCategory: { category: PatientCategory | null; visits: number; patients: number; revenue: string }[]
  /** The doctor the report was narrowed to; null means the whole clinic. */
  doctorId?: string | null
  /** Every day in the range, including empty ones, for the charts. */
  byDay?: { day: string; visits: number; collected: string }[]
}

/** The eight ABO/Rh groups the server accepts. A typo here is a clinical risk. */
export const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'] as const

export interface CollectionReport {
  date: string
  byMode: { mode: PaymentMode; count: number; total: string }[]
  /** Gross takings. Refunds are reported beside this, never netted into it. */
  total: string
  refunds: { byMode: { mode: PaymentMode; count: number; total: string }[]; total: string }
  /** Collected less refunds: the figure that should match the bank. */
  net: string
  /**
   * What should physically be in the drawer: cash taken less cash handed
   * back. Counting against `total` instead reads short by every refund, so
   * this is the figure a drawer count compares to.
   */
  expectedCashInDrawer: string
}

export interface FrequencyRow { count: number }
export interface DiagnosisRow extends FrequencyRow { diagnosis: string }
export interface NamedFrequencyRow extends FrequencyRow { name: string }

export interface OutstandingRow {
  visitId: string
  visitDate: string
  patientId: string
  patientName: string
  patientCode: string
  mobile: string | null
  patientCategory: PatientCategory | null
  doctorName: string
  netPayable: string
  amountPaid: string
  balance: string
}

export interface TimingRow {
  doctorId: string
  doctorName: string
  consults: number
  avgWaitMin: string
  avgConsultMin: string
}

export interface AuditEntry {
  id: string
  entity: string
  entityId: string | null
  action: string
  before: unknown
  after: unknown
  ip: string | null
  createdAt: string
  userId: string | null
  username: string | null
  userFullName: string | null
}

export interface BackupFile {
  name: string
  sizeBytes: number
  modifiedAt: string
}

/* ---------- test result capture (phase 5) ---------- */

export interface InvestigationRow {
  id: string
  testName: string
  urgency: TestUrgency
  status: TestStatus
  instructions: string | null
  reviewOn: string | null
  createdAt: string
  patientId: string
  patientName: string
  patientCode: string
  patientCategory: PatientCategory | null
  mobile: string | null
  doctorName: string
  visitId: string
  visitDate: string
  resultCount: number
}

export interface TestResult {
  id: string
  prescriptionTestId: string
  measuredAt: string
  value: string | null
  unit: string | null
  refRangeLow: string | null
  refRangeHigh: string | null
  flag: number | null
  labName: string | null
  notes: string | null
  filePath: string | null
  fileSizeBytes: number | null
  fileType: string | null
  createdAt: string
}

export interface InvestigationDetail {
  test: {
    id: string
    testName: string
    urgency: TestUrgency
    status: TestStatus
    instructions: string | null
    reviewOn: string | null
    prescriptionId: string
    visitId: string
    visitDate: string
    patientId: string
    patientName: string
    patientCode: string
    patientCategory: PatientCategory | null
    doctorId: string
    doctorName: string
  }
  results: TestResult[]
}

export const TEST_STATUS_LABEL: Record<TestStatus, string> = {
  advised: 'Advised',
  sample_taken: 'Sample taken',
  result_awaited: 'Awaiting result',
  result_received: 'Result received',
}

/* ---------- reminders (phase 5) ---------- */

export type ReminderKind = 'follow_up' | 'appointment_next_day'
export type ReminderStatus = 'pending' | 'sent' | 'failed' | 'cancelled'
export type ReminderChannel = 'sms' | 'whatsapp' | 'log'

export interface Reminder {
  id: string
  kind: ReminderKind
  channel: ReminderChannel
  status: ReminderStatus
  body: string
  toMobile: string
  scheduledFor: string
  sentAt: string | null
  providerRef: string | null
  errorMessage: string | null
  patientId: string
  patientName: string
  patientCode: string
  appointmentId: string | null
  visitId: string | null
}

export const REMINDER_KIND_LABEL: Record<ReminderKind, string> = {
  follow_up: 'Follow-up',
  appointment_next_day: 'Appointment (next day)',
}

export const REMINDER_STATUS_LABEL: Record<ReminderStatus, string> = {
  pending: 'Pending',
  sent: 'Sent',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

/**
 * Reads a File into base64 for the JSON result-upload endpoint. Kept in the
 * API client because it is only ever used by that one call.
 */
export async function fileToBase64(file: File): Promise<{ contentType: string; data: string }> {
  const buffer = await file.arrayBuffer()
  let binary = ''
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]!)
  return { contentType: file.type || 'application/octet-stream', data: btoa(binary) }
}

/** A clinic on this deployment, as the platform admin sees it: counts, never records. */
export interface PlatformClinic {
  id: string
  slug: string
  name: string
  status: 'active' | 'suspended'
  createdAt: string
  staff: number
  patients: number
  visitsLast30Days: number
  lastVisit: string | null
  admins: string | null
}
